import { isPlainObject } from "lodash";

interface AnyObject {
  [key: string]: any;
}

// Function to remove specified fields from an object, including nested objects
export const removeSpecifiedFields = <T extends AnyObject>(
  obj: AnyObject,
  fields: string[]
): T => {
  return Object.entries(obj).reduce((acc: any, [key, value]) => {
    if (!fields.includes(key)) {
      // Recursively process nested objects
      if (isPlainObject(value)) {
        const cleanedValue = removeSpecifiedFields(value, fields);
        if (Object.keys(cleanedValue).length > 0) {
          acc[key] = cleanedValue;
        }
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {} as T);
};

// Main function to clean optional form values
export const cleanOptionalFormValues = <T extends AnyObject>(
  obj: T,
  options?: {
    remove?: string[];
    ignore?: string[];
    notRemoveEmptyObjects?: boolean;
    notRemoveEmptyArrays?: boolean;
  }
): T => {
  // If fields are specified, remove them
  if (options?.remove && options?.remove.length > 0) {
    return removeSpecifiedFields(obj, options?.remove);
  }

  if (Array.isArray(obj)) {
    // Process arrays recursively
    return obj
      .map(item => cleanOptionalFormValues(item))
      .filter(
        item =>
          !(
            item === undefined ||
            item === null ||
            item === "" ||
            (isPlainObject(item) && Object.keys(item).length === 0) ||
            (Array.isArray(item) && item.length === 0)
          )
      ) as any;
  } else if (isPlainObject(obj)) {
    // Process objects recursively
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const cleanedValue = cleanOptionalFormValues(value);

      if (options?.ignore && options?.ignore.includes(key)) {
        return { ...acc, [key]: value };
      }

      if (
        Array.isArray(cleanedValue) &&
        cleanedValue.length === 0 &&
        options?.notRemoveEmptyArrays
      ) {
        return { ...acc, [key]: cleanedValue };
      }

      if (
        isPlainObject(cleanedValue) &&
        Object.keys(cleanedValue).length === 0 &&
        options?.notRemoveEmptyObjects
      ) {
        return { ...acc, [key]: cleanedValue };
      }

      if (
        cleanedValue === undefined ||
        cleanedValue === null ||
        cleanedValue === "" ||
        (isPlainObject(cleanedValue) &&
          Object.keys(cleanedValue).length === 0) ||
        (Array.isArray(cleanedValue) && cleanedValue.length === 0)
      ) {
        return acc;
      }

      return { ...acc, [key]: cleanedValue };
    }, {} as T);
  }

  return obj;
};
